import React, { useState } from 'react';
import { veoCreditAdminService, VeoCreditUserData } from '../../services/veoCreditAdminService';
import styles from './FuturisticAdmin.module.css';

interface VeoCreditAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: VeoCreditUserData;
  onSuccess: () => void;
}

const VeoCreditAdjustmentModal: React.FC<VeoCreditAdjustmentModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
}) => {
  // Default to 'grant' for users without VEO credits, 'adjust' for existing users
  const [adjustmentType, setAdjustmentType] = useState<'adjust' | 'grant'>(
    user.hasVeoCredits ? 'adjust' : 'grant'
  );
  const [credits, setCredits] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const creditAmount = parseInt(credits);
    if (isNaN(creditAmount) || creditAmount === 0) {
      setError('Please enter a valid credit amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please provide a reason for this adjustment');
      return;
    }

    // For adjustments, prevent making credits negative
    if (adjustmentType === 'adjust' && creditAmount < 0 && Math.abs(creditAmount) > user.credits) {
      setError(`Cannot deduct ${Math.abs(creditAmount)} credits. User only has ${user.credits} credits.`);
      return;
    }

    // For grants, only allow positive values
    if (adjustmentType === 'grant' && creditAmount <= 0) {
      setError('Grant amount must be positive');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (adjustmentType === 'grant') {
        await veoCreditAdminService.grantUserVeoCredits(user.userId, creditAmount, reason);
      } else {
        await veoCreditAdminService.adjustUserVeoCredits(user.userId, creditAmount, reason);
      }

      onSuccess();
      // Reset form
      setCredits('');
      setReason('');
      setAdjustmentType(user.hasVeoCredits ? 'adjust' : 'grant');
    } catch (err: any) {
      setError(err.message || 'Failed to adjust credits');
    } finally {
      setLoading(false);
    }
  };

  const getPreviewBalance = (): number => {
    const creditAmount = parseInt(credits) || 0;
    if (adjustmentType === 'grant') {
      return user.credits + creditAmount;
    }
    return Math.max(0, user.credits + creditAmount);
  };

  const getAdjustmentTypeColor = (type: 'adjust' | 'grant') => {
    return type === 'grant' ? '#10b981' : '#8b5cf6';
  };

  const quickReasons = adjustmentType === 'grant' 
    ? [
        'Initial VEO credit assignment',
        'Promotional grant',
        'Customer service compensation',
        'Beta testing reward',
        'Contest prize',
        'Account migration bonus'
      ]
    : [
        'Customer service adjustment',
        'Billing error correction',
        'Account cleanup',
        'Technical issue compensation',
        'Usage dispute resolution',
        'VEO credit reallocation'
      ];

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        border: '1px solid rgba(51, 65, 85, 0.5)',
        borderRadius: '16px',
        maxWidth: '600px',
        width: '100%',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '2rem',
          borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(16, 185, 129, 0.1))'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ 
                color: 'white', 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                margin: '0 0 0.5rem 0',
                background: 'linear-gradient(135deg, #8b5cf6, #10b981)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {user.hasVeoCredits ? '⚙️ Adjust VEO Credits' : '🎁 Assign VEO Credits'}
              </h2>
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '1rem' }}>
                {user.email}
              </p>
              <div style={{
                marginTop: '0.5rem',
                fontSize: '0.875rem',
                color: user.credits > 0 ? '#06b6d4' : '#f59e0b',
                backgroundColor: user.credits > 0 ? 'rgba(6, 182, 212, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                border: user.credits > 0 ? '1px solid rgba(6, 182, 212, 0.3)' : '1px solid rgba(245, 158, 11, 0.3)',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                Current: {veoCreditAdminService.formatCredits(user.credits)} credits
                {!user.hasVeoCredits && ' (New VEO User)'}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                padding: '0.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
          {/* Adjustment Type Selection */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#94a3b8', 
              fontSize: '0.875rem', 
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}>
              Adjustment Type
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                onClick={() => setAdjustmentType('adjust')}
                style={{
                  flex: 1,
                  padding: '1rem',
                  backgroundColor: adjustmentType === 'adjust' 
                    ? 'rgba(139, 92, 246, 0.2)' 
                    : 'rgba(51, 65, 85, 0.3)',
                  border: adjustmentType === 'adjust' 
                    ? '2px solid rgba(139, 92, 246, 0.5)' 
                    : '1px solid rgba(51, 65, 85, 0.5)',
                  borderRadius: '8px',
                  color: adjustmentType === 'adjust' ? '#8b5cf6' : '#94a3b8',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚙️</div>
                <div>Adjust Credits</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  Add or remove credits
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setAdjustmentType('grant')}
                style={{
                  flex: 1,
                  padding: '1rem',
                  backgroundColor: adjustmentType === 'grant' 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : 'rgba(51, 65, 85, 0.3)',
                  border: adjustmentType === 'grant' 
                    ? '2px solid rgba(16, 185, 129, 0.5)' 
                    : '1px solid rgba(51, 65, 85, 0.5)',
                  borderRadius: '8px',
                  color: adjustmentType === 'grant' ? '#10b981' : '#94a3b8',
                  cursor: 'pointer',
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🎁</div>
                <div>Grant Credits</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                  Gift free credits
                </div>
              </button>
            </div>
          </div>

          {/* Credit Amount */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#94a3b8', 
              fontSize: '0.875rem', 
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}>
              {adjustmentType === 'grant' ? 'Credits to Grant' : 'Credit Adjustment'}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder={adjustmentType === 'grant' ? 'Enter positive amount' : 'Enter amount (+ to add, - to remove)'}
                min={adjustmentType === 'grant' ? '1' : undefined}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                  border: `2px solid ${getAdjustmentTypeColor(adjustmentType)}40`,
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                required
              />
              {credits && !isNaN(parseInt(credits)) && (
                <div style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '0.875rem',
                  color: parseInt(credits) > 0 ? '#10b981' : '#ef4444',
                  fontWeight: '500'
                }}>
                  {parseInt(credits) > 0 ? '+' : ''}{parseInt(credits)}
                </div>
              )}
            </div>
            
            {/* Preview Balance */}
            {credits && !isNaN(parseInt(credits)) && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                backgroundColor: `${getAdjustmentTypeColor(adjustmentType)}10`,
                border: `1px solid ${getAdjustmentTypeColor(adjustmentType)}30`,
                borderRadius: '6px',
                fontSize: '0.875rem'
              }}>
                <div style={{ color: '#94a3b8', marginBottom: '0.25rem' }}>
                  Preview Balance:
                </div>
                <div style={{ 
                  color: getAdjustmentTypeColor(adjustmentType), 
                  fontWeight: '600',
                  fontSize: '1rem'
                }}>
                  {user.credits} → {getPreviewBalance()} credits
                </div>
              </div>
            )}
          </div>

          {/* Reason */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ 
              display: 'block', 
              color: '#94a3b8', 
              fontSize: '0.875rem', 
              marginBottom: '0.75rem',
              fontWeight: '500'
            }}>
              Reason for Adjustment
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter the reason for this credit adjustment..."
              rows={3}
              style={{
                width: '100%',
                padding: '1rem',
                backgroundColor: 'rgba(15, 23, 42, 0.5)',
                border: '1px solid rgba(51, 65, 85, 0.5)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none'
              }}
              required
            />
            
            {/* Quick Reason Buttons */}
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ 
                color: '#64748b', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem' 
              }}>
                Quick reasons:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {quickReasons.map((quickReason) => (
                  <button
                    key={quickReason}
                    type="button"
                    onClick={() => setReason(quickReason)}
                    style={{
                      backgroundColor: 'rgba(100, 116, 139, 0.2)',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                      color: '#64748b',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(100, 116, 139, 0.3)';
                      e.currentTarget.style.color = '#94a3b8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(100, 116, 139, 0.2)';
                      e.currentTarget.style.color = '#64748b';
                    }}
                  >
                    {quickReason}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#ef4444',
              fontSize: '0.875rem'
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                backgroundColor: 'rgba(100, 116, 139, 0.2)',
                border: '1px solid rgba(100, 116, 139, 0.3)',
                color: '#64748b',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                opacity: loading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={loading || !credits || !reason.trim()}
              style={{
                backgroundColor: loading || !credits || !reason.trim() 
                  ? 'rgba(100, 116, 139, 0.2)' 
                  : `${getAdjustmentTypeColor(adjustmentType)}40`,
                border: `1px solid ${loading || !credits || !reason.trim() 
                  ? 'rgba(100, 116, 139, 0.3)' 
                  : `${getAdjustmentTypeColor(adjustmentType)}60`}`,
                color: loading || !credits || !reason.trim() 
                  ? '#64748b' 
                  : getAdjustmentTypeColor(adjustmentType),
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: (loading || !credits || !reason.trim()) ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid currentColor', 
                    borderTop: '2px solid transparent', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  Processing...
                </>
              ) : (
                <>
                  {adjustmentType === 'grant' ? '🎁' : '⚙️'}
                  {adjustmentType === 'grant' ? 'Grant Credits' : 'Adjust Credits'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VeoCreditAdjustmentModal;
